import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "selectedContainer"]

  connect() {
    // Track expanded states by index
    this.expandedStates = new Set()
  }

  add() {
    const input = this.inputTarget
    const value = input.value.trim()

    if (!value) return

    // Check if already added (compare variable names for both simple and complex conditions)
    const existingConditions = this.selectedContainerTarget.querySelectorAll("[data-condition]")
    for (const existing of existingConditions) {
      const conditionData = this.parseConditionData(existing)
      if (conditionData.variable === value) {
        input.value = ""
        return
      }
    }

    // Create a presence check condition by default
    const condition = { variable: value, type: "presence" }
    const conditionElement = this.createConditionElement(condition)
    this.selectedContainerTarget.appendChild(conditionElement)

    // Clear the input
    input.value = ""
  }

  remove(event) {
    event.stopPropagation()
    const conditionEl = event.target.closest("[data-condition]")
    conditionEl.remove()
  }

  toggleExpand(event) {
    // Don't toggle if clicking on buttons or inputs
    if (event.target.closest("button") || event.target.closest("input") || event.target.closest("label")) {
      return
    }

    const conditionEl = event.target.closest("[data-condition]")
    const isExpanded = conditionEl.dataset.expanded === "true"

    if (isExpanded) {
      this.collapse(conditionEl)
    } else {
      this.expand(conditionEl)
    }
  }

  expand(conditionEl) {
    conditionEl.dataset.expanded = "true"
    const expandedContent = conditionEl.querySelector("[data-expanded-content]")
    const collapsedContent = conditionEl.querySelector("[data-collapsed-content]")
    if (expandedContent) expandedContent.classList.remove("hidden")
    if (collapsedContent) collapsedContent.classList.add("hidden")
  }

  collapse(conditionEl) {
    conditionEl.dataset.expanded = "false"
    const expandedContent = conditionEl.querySelector("[data-expanded-content]")
    const collapsedContent = conditionEl.querySelector("[data-collapsed-content]")
    if (expandedContent) expandedContent.classList.add("hidden")
    if (collapsedContent) collapsedContent.classList.remove("hidden")
    // Update collapsed display
    this.updateCollapsedDisplay(conditionEl)
  }

  setConditionType(event) {
    const conditionEl = event.target.closest("[data-condition]")
    const conditionType = event.target.value
    const valuesSection = conditionEl.querySelector("[data-values-section]")

    if (conditionType === "values") {
      valuesSection.classList.remove("hidden")
    } else {
      valuesSection.classList.add("hidden")
    }

    this.updateHiddenInput(conditionEl)
  }

  addValue(event) {
    const conditionEl = event.target.closest("[data-condition]")
    const valueInput = conditionEl.querySelector("[data-value-input]")
    const value = valueInput.value.trim()

    if (!value) return

    // Check if value already exists
    const valuesContainer = conditionEl.querySelector("[data-values-container]")
    const existingValues = valuesContainer.querySelectorAll("[data-value]")
    for (const existing of existingValues) {
      if (existing.dataset.value === value) {
        valueInput.value = ""
        return
      }
    }

    // Create value tag
    const valueTag = document.createElement("span")
    valueTag.className = "inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-blue-300 text-blue-800 rounded text-xs"
    valueTag.dataset.value = value
    valueTag.innerHTML = `
      ${this.escapeHtml(value)}
      <button type="button" class="text-blue-600 hover:text-blue-800" data-action="click->visibility-variables#removeValue">&times;</button>
    `

    valuesContainer.appendChild(valueTag)
    valueInput.value = ""

    this.updateHiddenInput(conditionEl)
  }

  removeValue(event) {
    event.stopPropagation()
    const valueTag = event.target.closest("[data-value]")
    const conditionEl = event.target.closest("[data-condition]")
    valueTag.remove()
    this.updateHiddenInput(conditionEl)
  }

  updateHiddenInput(conditionEl) {
    const hiddenInput = conditionEl.querySelector("input[type=hidden][name='report_template_element[visibility_variables][]']")
    const conditionData = this.parseConditionData(conditionEl)
    hiddenInput.value = this.serializeCondition(conditionData)
  }

  updateCollapsedDisplay(conditionEl) {
    const conditionData = this.parseConditionData(conditionEl)
    const collapsedText = conditionEl.querySelector("[data-collapsed-text]")
    if (collapsedText) {
      collapsedText.textContent = this.formatCollapsedDisplay(conditionData)
    }
  }

  parseConditionData(conditionEl) {
    const variable = conditionEl.dataset.variable
    const typeRadio = conditionEl.querySelector("input[name^='condition_type']:checked")
    const type = typeRadio ? typeRadio.value : "presence"

    const values = []
    const valueTags = conditionEl.querySelectorAll("[data-value]")
    valueTags.forEach(tag => values.push(tag.dataset.value))

    return { variable, type, values }
  }

  serializeCondition(conditionData) {
    if (conditionData.type === "presence") {
      // Simple string for presence checks (backwards compatible)
      return conditionData.variable
    } else {
      // JSON for value matching
      return JSON.stringify({
        variable: conditionData.variable,
        values: conditionData.values
      })
    }
  }

  formatCollapsedDisplay(conditionData) {
    if (conditionData.type === "presence" || conditionData.values.length === 0) {
      return conditionData.variable
    } else {
      const valueCount = conditionData.values.length
      return `${conditionData.variable}: ${valueCount} value${valueCount !== 1 ? "s" : ""}`
    }
  }

  createConditionElement(condition) {
    const isValueMatch = condition.type === "values" || (condition.values && condition.values.length > 0)
    const uniqueId = Math.random().toString(36).substring(2, 9)

    const wrapper = document.createElement("div")
    wrapper.className = "inline-block"
    wrapper.dataset.condition = "true"
    wrapper.dataset.variable = condition.variable
    wrapper.dataset.expanded = "false"

    const serializedValue = this.serializeCondition({
      variable: condition.variable,
      type: isValueMatch ? "values" : "presence",
      values: condition.values || []
    })

    wrapper.innerHTML = `
      <input type="hidden" name="report_template_element[visibility_variables][]" value="${this.escapeHtml(serializedValue)}" form="report_element_form">

      <!-- Collapsed state -->
      <div data-collapsed-content class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm cursor-pointer hover:bg-blue-200 transition-colors" data-action="click->visibility-variables#toggleExpand">
        <span class="text-blue-600 text-xs">▶</span>
        <span data-collapsed-text>${this.escapeHtml(this.formatCollapsedDisplay({ variable: condition.variable, type: isValueMatch ? "values" : "presence", values: condition.values || [] }))}</span>
        <button type="button" class="text-blue-600 hover:text-blue-800 ml-1" data-action="click->visibility-variables#remove">&times;</button>
      </div>

      <!-- Expanded state -->
      <div data-expanded-content class="hidden bg-blue-50 border border-blue-200 rounded-lg p-3 min-w-64">
        <div class="flex justify-between items-center mb-2">
          <span class="font-medium text-blue-900">${this.escapeHtml(condition.variable)}</span>
          <div class="flex items-center gap-1">
            <button type="button" class="text-blue-600 hover:text-blue-800 text-sm" data-action="click->visibility-variables#toggleExpand">▼</button>
            <button type="button" class="text-blue-600 hover:text-blue-800" data-action="click->visibility-variables#remove">&times;</button>
          </div>
        </div>

        <div class="space-y-2">
          <label class="flex items-center gap-2 text-sm text-blue-800 cursor-pointer">
            <input type="radio" name="condition_type_${uniqueId}" value="presence" ${!isValueMatch ? "checked" : ""} data-action="change->visibility-variables#setConditionType" class="text-blue-600">
            Show if variable has any value
          </label>
          <label class="flex items-center gap-2 text-sm text-blue-800 cursor-pointer">
            <input type="radio" name="condition_type_${uniqueId}" value="values" ${isValueMatch ? "checked" : ""} data-action="change->visibility-variables#setConditionType" class="text-blue-600">
            Show if variable matches one of:
          </label>

          <div data-values-section class="${isValueMatch ? "" : "hidden"} pl-6 space-y-2">
            <div data-values-container class="flex flex-wrap gap-1">
              ${(condition.values || []).map(v => `
                <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-blue-300 text-blue-800 rounded text-xs" data-value="${this.escapeHtml(v)}">
                  ${this.escapeHtml(v)}
                  <button type="button" class="text-blue-600 hover:text-blue-800" data-action="click->visibility-variables#removeValue">&times;</button>
                </span>
              `).join("")}
            </div>
            <div class="flex gap-1">
              <input type="text" data-value-input placeholder="Add value..." class="flex-1 px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500">
              <button type="button" class="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700" data-action="click->visibility-variables#addValue">Add</button>
            </div>
          </div>
        </div>
      </div>
    `

    return wrapper
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
